import { format } from "date-fns"
import { CalendarIcon, XIcon } from "lucide-react"
import { useState } from "react"
import { Button } from "~/components/ui/button"
import { Calendar } from "~/components/ui/calendar"
import {
	Popover,
	PopoverContent,
	PopoverTrigger
} from "~/components/ui/popover"

const withDefaultDueTime = (date: Date) => {
	const dueDate = new Date(date)
	dueDate.setHours(23, 59, 0, 0)
	return dueDate
}

export function IssueDueDatePicker({
	value,
	onChange,
	disabled,
	shape = "default",
	showBorder = false,
	emptyLabel = "No due date",
	clearLabel = "Clear date",
	ariaLabel,
	minDate,
	maxDate
}: {
	value: Date | null | undefined
	onChange: (date: Date | null) => void
	disabled?: boolean
	shape?: "default" | "pill"
	showBorder?: boolean
	emptyLabel?: string
	clearLabel?: string
	ariaLabel?: string
	minDate?: Date | null
	maxDate?: Date | null
}) {
	const [open, setOpen] = useState(false)

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					shape={shape}
					variant={showBorder ? "picker-outline" : "picker"}
					role="combobox"
					aria-label={ariaLabel}
					aria-expanded={open}
					size="sm"
					disabled={disabled}
					className="w-full"
				>
					<CalendarIcon className="size-4" />
					{value ? format(value, "PPP") : <span>{emptyLabel}</span>}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="bg-background-secondary p-1 justify-center"
				align="center"
				size="fit"
			>
				<Calendar
					mode="single"
					disabled={[
						...(minDate ? [{ before: minDate }] : []),
						...(maxDate ? [{ after: maxDate }] : [])
					]}
					selected={value ?? undefined}
					defaultMonth={value ?? undefined}
					onSelect={(date) => {
						onChange(date ? withDefaultDueTime(date) : null)
						setOpen(false)
					}}
				/>
				<Button
					variant="ghost"
					type="button"
					size="sm"
					className="text-icons hover:text-text-main w-full"
					onClick={() => onChange(null)}
					disabled={!value || disabled}
				>
					<XIcon className="size-3" /> {clearLabel}
				</Button>
			</PopoverContent>
		</Popover>
	)
}
